/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getTodayDate } from "../../../utils/dateAndTime";
import AIEventAssistant from "./AIEventAssistant";
import ImageUploader from "../../ui/ImageUploader";
import LocationAutocomplete from "../../ui/LocationAutocomplete";
import { getFunctions, httpsCallable } from "firebase/functions";
import { validateAndResizeImage } from "../../../utils/imageUtils";
import { useAuth } from "../../../context/AuthContext";

// Add this constant at the top of the file for consistent color usage
const MINT_GREEN = {
  base: 'bg-success-50 text-success-600 border border-success-200',
  hover: 'hover:bg-success-100 hover:text-success-700 hover:border-success-300',
  active: 'active:bg-success-100 active:border-success-400'
};

const CreateEventContent1 = ({
  handleContinue,
  handleCancel,
  handleSaveForLater,
  formState,
  setFormState,
  handleUploadImageClick,
  UserID,
  eventData,
  updateEventData,
}) => {
  const { subscriptionTier } = useAuth();
  const isPro = subscriptionTier === 'pro';
  const navigate = useNavigate();

  // Date validation state
  const [dateErrors, setDateErrors] = useState({
    endDate: "",
    applicationDeadline: "",
    loadInDate: "",
    loadOutDate: ""
  });

  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiDescriptionLoading, setAiDescriptionLoading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [generatedDescription, setGeneratedDescription] = useState('');
  const [isAIProBannerHovered, setIsAIProBannerHovered] = useState(false);
  const [showAIProModal, setShowAIProModal] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [flyerReading, setFlyerReading] = useState(false);
  const [flyerExtracted, setFlyerExtracted] = useState(false);

  
  const { t } = useTranslation();

  const [showAIProBanner, setShowAIProBanner] = useState(() => {
    const dismissedAt = localStorage.getItem('aiProBannerDismissed');
    if (!dismissedAt) return true;
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - parseInt(dismissedAt) > oneWeek;
  });

  const dismissAIProBanner = () => {
    localStorage.setItem('aiProBannerDismissed', Date.now().toString());
    setShowAIProBanner(false);
  };

  const handleApplyAISuggestions = (generatedEvent) => {
    setFormState(prev => ({
      ...prev,
      eventName: generatedEvent.eventName || prev.eventName,
      eventType: generatedEvent.eventType || prev.eventType,
      location: generatedEvent.location || prev.location,
      description: generatedEvent.description || prev.description,
      startDate: generatedEvent.startDate || prev.startDate,
      startTime: generatedEvent.startTime || prev.startTime,
      endDate: generatedEvent.endDate || prev.endDate,
      endTime: generatedEvent.endTime || prev.endTime,
      applicationDeadline: generatedEvent.applicationDeadline || prev.applicationDeadline,
      loadInDate: generatedEvent.loadInDate || prev.loadInDate,
      loadInTime: generatedEvent.loadInTime || prev.loadInTime,
      loadOutDate: generatedEvent.loadOutDate || prev.loadOutDate,
      loadOutTime: generatedEvent.loadOutTime || prev.loadOutTime,
    }));
    
    // Also update selected seller types and capacity if provided
    if (generatedEvent.sellerTypes || generatedEvent.sellerCapacity) {
      updateEventData({
        sellerTypes: generatedEvent.sellerTypes,
        sellerCapacity: generatedEvent.sellerCapacity,
      });
    }
  };

  const handleSaveForLaterClick = () => {
    updateEventData({
      ...formState,
      eventId: eventData.eventId,
      userId: UserID,
      activeStep: 0,
      deadlineDate: formState.applicationDeadline,
      socialMediaHandles: {
        facebook: formState.eoEventFacebookAccount,
        instagram: formState.eoEventInstagramAccount,
        tiktok: formState.eoEventTikTokAccount,
      },
    });
    handleSaveForLater();
  };

  const handleSaveContinue = () => {
    updateEventData({
      ...formState,
      userId: UserID,
      deadlineDate: formState.applicationDeadline,
      socialMediaHandles: {
        facebook: formState.eoEventFacebookAccount,
        instagram: formState.eoEventInstagramAccount,
        tiktok: formState.eoEventTikTokAccount,
      },
    });
    handleContinue();
  };

  const readFlyerWithAI = async (file) => {
    if (!isPro || !file) return;
    setFlyerReading(true);
    setFlyerExtracted(false);
    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
      });
      const fns = getFunctions();
      const aiReadFlyer = httpsCallable(fns, 'aiReadEventFlyer');
      const result = await aiReadFlyer({ imageBase64: base64, mimeType: file.type });
      const data = result.data?.data;

      if (!data) return;

      setFormState(prev => ({
        ...prev,
        ...(data.eventName && !prev.eventName ? { eventName: data.eventName } : {}),
        ...(data.location && !prev.location ? { location: data.location } : {}),
        ...(data.description && !prev.description ? { description: data.description } : {}),
        ...(data.eventType && !prev.eventType ? { eventType: data.eventType } : {}),
        ...(data.startDate && !prev.startDate ? { startDate: data.startDate } : {}),
        ...(data.startTime && !prev.startTime ? { startTime: data.startTime } : {}),
        ...(data.endDate && !prev.endDate ? { endDate: data.endDate } : {}),
        ...(data.endTime && !prev.endTime ? { endTime: data.endTime } : {}),
      }));
      setFlyerExtracted(true);
      setTimeout(() => setFlyerExtracted(false), 6000);
    } catch (err) {
      console.error('Flyer reading failed:', err);
    } finally {
      setFlyerReading(false);
    }
  };

  const generateAIDescription = async (overridePrompt) => {
    if (!isPro) { setShowAIProModal(true); return; }
    if (!formState.eventName) return;

    const promptToUse = overridePrompt !== undefined ? overridePrompt : aiPrompt;

    // Push user message to chat
    if (promptToUse) {
      setAiMessages(prev => [...prev, { role: 'user', text: promptToUse }]);
    }
    setAiPrompt('');
    setAiDescriptionLoading(true);
    setGeneratedDescription('');

    try {
      const functions = getFunctions();
      const aiGenerateDescriptionFn = httpsCallable(functions, 'aiGenerateDescription');
      const result = await aiGenerateDescriptionFn({
        eventName: formState.eventName,
        eventType: formState.eventType || 'Event',
        location: formState.location || '',
        customPrompt: promptToUse || ''
      });
      const data = result.data;
      let description;
      if (data.success && data.description) {
        description = data.description;
      } else {
        throw new Error(data.error || 'Failed to generate description');
      }

      setGeneratedDescription(description);
      setAiMessages(prev => [...prev, { role: 'ai', text: description }]);
    } catch (error) {
      console.error('Error generating description:', error);
      const errMsg = 'Unable to generate description. Please try again.';
      setGeneratedDescription(errMsg);
      setAiMessages(prev => [...prev, { role: 'ai', text: errMsg }]);
    } finally {
      setAiDescriptionLoading(false);
    }
  };


  const toggleVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Voice input is not supported in this browser.'); return; }
    if (!isPro) { setShowAIProModal(true); return; }

    if (isListening) {
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setAiPrompt(prev => (prev ? prev + ' ' + transcript : transcript));
    };

    recognition.start();
  };

  const validateDateTime = (field, newValue) => {
    const today = getTodayDate();
    const {
      startDate,
      startTime,
      endDate,
      endTime,
      loadInDate,
      loadInTime,
      loadOutTime,
    } = formState;

    switch (field) {
      case "startDate":
        if (new Date(newValue) < new Date(today)) {
          return "Event start date cannot be before today";
        }
        break;

      case "endDate":
        if (startDate) {
          const startDateTime = new Date(`${startDate} ${startTime || "00:00"}`);
          const endDateTime = new Date(`${newValue} ${endTime || "23:59"}`);
          if (startDateTime >= endDateTime) {
            return "End date & time must be after start date & time";
          }
        }
        break;

      case "loadInDate":
        if (startDate) {
          const loadInDateTime = new Date(`${newValue} ${loadInTime || "00:00"}`);
          const eventStartDateTime = new Date(`${startDate} ${startTime || "00:00"}`);
          if (loadInDateTime > eventStartDateTime) {
            return "Load in must be on or before event start time";
          }
        }
        break;

      case "loadOutDate":
        if (loadInDate) {
          const loadInDateTime = new Date(`${loadInDate} ${loadInTime || "00:00"}`);
          const loadOutDateTime = new Date(`${newValue} ${loadOutTime || "23:59"}`);
          if (loadOutDateTime <= loadInDateTime) {
            return "Load out must be after load in time";
          }
        }
        if (endDate) {
          const loadOutDateTime = new Date(`${newValue} ${loadOutTime || "23:59"}`);
          const eventEndDateTime = new Date(`${endDate} ${endTime || "23:59"}`);
          if (loadOutDateTime < eventEndDateTime) {
            return "Load out must be on or after event end time";
          }
        }
        break;

      case "applicationDeadline":
        if (startDate && new Date(newValue) > new Date(startDate)) {
          return "Application deadline must be on or before event start";
        }
        break;

      default:
        return "";
    }

    return "";
  };

  const handleDateTimeChange = (field, newValue) => {
    // Update formState
    setFormState((prevState) => ({
      ...prevState,
      [field]: newValue,
    }));

    // Validate the updated value
    const error = validateDateTime(field, newValue);

    // Update dateErrors state
    setDateErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  // Generalized Change Handler
  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  //state handlers
  const formStateHandlers = {
    eventName: handleChange("eventName"),
    location: handleChange("location"),
    description: handleChange("description"),
    startDate: (e) => handleDateTimeChange("startDate", e.target.value),
    startTime: handleChange("startTime"),
    endDate: (e) => handleDateTimeChange("endDate", e.target.value),
    endTime: handleChange("endTime"),
    recurringEvent: () =>
      setFormState((prev) => ({ ...prev, recurringEvent: !prev.recurringEvent })),
    applicationDeadline: handleChange("applicationDeadline"),

    // For date-time inputs like loadInDate, loadInTime, loadOutDate, loadOutTime
    loadInDate: (e) => handleDateTimeChange("loadInDate", e.target.value),
    loadInTime: (e) => handleDateTimeChange("loadInTime", e.target.value),
    loadOutDate: (e) => handleDateTimeChange("loadOutDate", e.target.value),
    loadOutTime: (e) => handleDateTimeChange("loadOutTime", e.target.value),

    eoEventWebsiteURL: handleChange("eoEventWebsiteURL"),
    eoEventFacebookAccount: handleChange("eoEventFacebookAccount"),
    eoEventInstagramAccount: handleChange("eoEventInstagramAccount"),
    eoEventTikTokAccount: handleChange("eoEventTikTokAccount"),
  };

  // Simple location input handler
  const handleLocationChange = (e) => {
    setFormState(prev => ({ ...prev, location: e.target.value }));
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-3 pb-4 sm:px-6 sm:pb-8">
      {/* AI Assistant Modal */}
      <AIEventAssistant
        isOpen={showAIAssistant}
        onClose={() => setShowAIAssistant(false)}
        onApplySuggestions={handleApplyAISuggestions}
        currentFormState={formState}
      />

      {/* Page Title */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-grey-900">{t('createEvent.pageTitle')}</h1>
        <p className="text-sm text-grey-500 mt-1">{t('createEvent.pageSubtitle')}</p>
      </div>

      {/* AI Pro banner — only shown to Basic users as upsell */}
      {!isPro && showAIProBanner && (
        <div
          className="relative bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg transition-all duration-300 overflow-hidden mb-6 cursor-pointer group hover:border-violet-300 hover:shadow-md"
          onClick={() => setShowAIProModal(true)}
          onMouseEnter={() => setIsAIProBannerHovered(true)}
          onMouseLeave={() => setIsAIProBannerHovered(false)}
        >
          <div className="px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative w-5 h-5 flex items-center justify-center flex-shrink-0">
                <div className="absolute w-5 h-5 rounded-full bg-violet-400/30 animate-ai-breathe" />
                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-violet-400 to-purple-600 animate-ai-breathe shadow-sm" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-violet-900 tracking-wide uppercase">AI Writing</span>
                <span className="text-[10px] font-medium text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded uppercase tracking-wider">Pro Feature</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-medium text-violet-500 transition-opacity duration-200 ${isAIProBannerHovered ? 'opacity-100' : 'opacity-0'}`}>
                Unlock with Pro →
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); dismissAIProBanner(); }}
                className="text-violet-300 hover:text-violet-500 text-sm leading-none p-1 transition-colors"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Pro Feature Modal */}
      {showAIProModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
            onClick={() => setShowAIProModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all animate-in zoom-in-95 duration-200">
            {/* Minimal luxury header */}
            <div className="bg-gradient-to-br from-grey-900 via-grey-800 to-black px-8 py-10 text-center relative overflow-hidden">
              {/* Subtle accent line */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-violet-400 to-transparent" />
              
              {/* Refined orb */}
              <div className="relative w-12 h-12 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full bg-violet-500/20 animate-pulse" />
                <div className="absolute inset-1 rounded-full bg-gradient-to-br from-violet-400 to-purple-600 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              
              <p className="text-[10px] font-medium text-violet-300 uppercase tracking-[0.3em] mb-3">{t('createEvent.aiProIntroducing')}</p>
              <h2 className="text-3xl font-light text-white tracking-tight mb-2">{t('createEvent.aiProHeadline')}</h2>
              <p className="text-grey-400 text-sm font-light tracking-wide">{t('createEvent.aiProTagline')}</p>
            </div>
            
            {/* Content */}
            <div className="px-8 py-8">
              <p className="text-grey-900 text-center text-lg font-light leading-relaxed mb-8">
                {t('createEvent.aiProDescribeVision')} <span className="font-medium">{t('createEvent.aiProWeBuildIt')}</span>
              </p>

              {/* Features list - minimal, no emojis */}
              <div className="space-y-4 mb-8">
                {[
                  t('createEvent.aiProFeature1'),
                  t('createEvent.aiProFeature2'),
                  t('createEvent.aiProFeature3'),
                  t('createEvent.aiProFeature4')
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-4 text-sm text-grey-600">
                    <div className="w-1 h-1 rounded-full bg-violet-500 flex-shrink-0" />
                    <span className="font-light">{feature}</span>
                  </div>
                ))}
              </div>
              
              {/* Pricing callout */}
              <div className="border-t border-grey-100 pt-6 text-center">
                <p className="text-[10px] font-medium text-grey-400 uppercase tracking-[0.2em] mb-1">Included in Pro — $249/mo or $2,499/yr</p>
                <p className="text-sm text-grey-900 font-medium">0% platform fee + full AI suite</p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 pb-8 space-y-2">
              <button
                type="button"
                onClick={() => { setShowAIProModal(false); navigate('/eo-paywall'); }}
                className="w-full py-3.5 px-4 bg-grey-900 text-white text-sm font-semibold rounded-lg hover:bg-black transition-colors"
              >
                Upgrade to Pro
              </button>
              <button
                type="button"
                onClick={() => setShowAIProModal(false)}
                className="w-full py-2.5 px-4 text-grey-500 text-sm font-medium rounded-lg hover:text-grey-700 transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Card */}
      <div className="bg-white rounded-xl border border-grey-200 p-3 sm:p-6 space-y-4 sm:space-y-6">
        {/* Event Cover Photo */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-grey-900">
            {t('createEvent.eventCoverPhoto')}
          </label>
          <ImageUploader
            variant="banner"
            value={formState.eventCoverPhoto}
            onChange={async (imageData) => {
              if (imageData && imageData.file) {
                try {
                  const resizedFile = await validateAndResizeImage(imageData.file);
                  setFormState(prev => ({
                    ...prev,
                    eventCoverPhoto: resizedFile
                  }));
                  // Pro: auto-read flyer with AI
                  if (isPro) readFlyerWithAI(imageData.file);
                } catch (error) {
                  console.error("Image validation/resize failed:", error.message);
                  alert(error.message);
                }
              } else {
                setFormState(prev => ({
                  ...prev,
                  eventCoverPhoto: ""
                }));
                setFlyerExtracted(false);
              }
            }}
            helperText={t('createEvent.coverPhotoHelper')}
          />
          {/* AI flyer reading status — Pro only */}
          {isPro && flyerReading && (
            <div className="flex items-center gap-2 text-xs text-violet-600 mt-1">
              <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
              Reading your flyer with AI…
            </div>
          )}
          {isPro && flyerExtracted && !flyerReading && (
            <div className="flex items-center gap-1.5 text-xs text-green-600 mt-1">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>
              ✨ Event details auto-filled from your flyer!
            </div>
          )}
        </div>

        {/* Two Column Grid for Name and Location */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Event Name */}
          <div className="space-y-2">
            <label htmlFor="eventName" className="block text-sm font-medium text-grey-900">
              {t('createEvent.eventName')}
            </label>
            <input
              id="eventName"
              type="text"
              value={formState.eventName}
              onChange={formStateHandlers.eventName}
              className="w-full px-3 py-2.5 border border-grey-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm"
              placeholder={eventData?.eventName || t('createEvent.eventNamePlaceholder')}
            />
          </div>

          {/* Location Input with Autocomplete */}
          <div className="space-y-2">
            <LocationAutocomplete
              label={t('createEvent.location')}
              value={formState.location}
              onChange={(value) => {
                setFormState(prev => ({ ...prev, location: value }));
              }}
              onSelect={(location) => {
                if (location) {
                  setFormState(prev => ({
                    ...prev,
                    location: location.address,
                    locationDetails: {
                      city: location.city,
                      state: location.state,
                      country: location.country,
                      postcode: location.postcode,
                      lat: location.lat,
                      lon: location.lon,
                    }
                  }));
                }
              }}
              placeholder={t('createEvent.locationPlaceholder')}
            />
          </div>
        </div>

        {/* Event Description */}
        <div className="space-y-3">
          <label htmlFor="description" className="block text-sm font-medium text-grey-900">
            {t('createEvent.eventDescription')}
          </label>
          
          {/* Textarea */}
          <textarea
            id="description"
            rows="4"
            value={formState.description}
            onChange={formStateHandlers.description}
            className="w-full p-3 border border-grey-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none text-sm"
            placeholder={t('createEvent.descriptionPlaceholder')}
          ></textarea>
          
          {/* AI Writing Assistant */}
          {!aiChatOpen ? (
            /* Trigger row */
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!isPro) { setShowAIProModal(true); return; }
                  if (!formState.eventName) return;
                  setAiChatOpen(true);
                }}
                disabled={!formState.eventName && isPro}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-600 hover:text-pebble-red transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                </svg>
                {isPro ? 'Write with AI' : '✨ Write with AI — Pro'}
              </button>
              {!formState.eventName && isPro && (
                <span className="text-xs text-grey-400">Enter an event name first</span>
              )}
            </div>
          ) : (
            /* Chat panel */
            <div className="border border-grey-200 rounded-xl overflow-hidden shadow-sm bg-white">
              {/* Panel header */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-grey-50 border-b border-grey-100">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                  </div>
                  <span className="text-xs font-semibold text-grey-700">AI Writing Assistant</span>
                </div>
                <button
                  type="button"
                  onClick={() => { setAiChatOpen(false); setAiMessages([]); setAiPrompt(''); setGeneratedDescription(''); }}
                  className="text-grey-400 hover:text-grey-600 text-lg leading-none transition"
                >×</button>
              </div>

              {/* Messages area */}
              <div className="p-4 space-y-3 min-h-[100px] max-h-[220px] overflow-y-auto">
                {aiMessages.length === 0 ? (
                  /* Empty state */
                  <p className="text-xs text-grey-400">Describe what you want — or just hit send and I'll write one for you.</p>
                ) : (
                  aiMessages.map((msg, i) => (
                    <div key={i} className={`flex flex-col gap-1 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                      <div className={`max-w-[90%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-grey-900 text-white rounded-tr-none'
                          : 'bg-grey-100 text-grey-800 rounded-tl-none'
                      }`}>
                        {msg.text}
                      </div>
                      {/* Use this — only on last AI message */}
                      {msg.role === 'ai' && i === aiMessages.length - 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormState(prev => ({ ...prev, description: msg.text }));
                            setAiChatOpen(false);
                            setAiMessages([]);
                            setGeneratedDescription('');
                            setAiPrompt('');
                          }}
                          className="text-xs font-medium text-violet-600 hover:text-violet-800 flex items-center gap-1 transition ml-1"
                        >
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M5 10l7-7m0 0l7 7m-7-7v18" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          Use this description
                        </button>
                      )}
                    </div>
                  ))
                )}

                {/* Typing indicator */}
                {aiDescriptionLoading && (
                  <div className="flex items-start gap-1">
                    <div className="bg-grey-100 rounded-2xl rounded-tl-none px-4 py-3 flex gap-1 items-center">
                      <span className="w-1.5 h-1.5 bg-grey-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}} />
                      <span className="w-1.5 h-1.5 bg-grey-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
                      <span className="w-1.5 h-1.5 bg-grey-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
                    </div>
                  </div>
                )}
              </div>

              {/* Input row */}
              <div className="flex items-center gap-2 px-3 py-2.5 border-t border-grey-100 bg-white">
                <input
                  type="text"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !aiDescriptionLoading) generateAIDescription();
                  }}
                  placeholder={aiMessages.length > 0 ? 'Ask for changes, a different tone…' : 'Describe your event vibe…'}
                  disabled={aiDescriptionLoading}
                  className="flex-1 text-sm text-grey-700 placeholder:text-grey-400 focus:outline-none bg-transparent"
                />
                {/* Voice button */}
                <button
                  type="button"
                  onClick={toggleVoice}
                  title={isListening ? 'Stop recording' : 'Speak your prompt'}
                  className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all ${
                    isListening ? 'bg-red-100 text-red-500 animate-pulse' : 'text-grey-400 hover:text-grey-600 hover:bg-grey-100'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {/* Send button */}
                <button
                  type="button"
                  onClick={() => generateAIDescription()}
                  disabled={aiDescriptionLoading}
                  className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-grey-900 text-white hover:bg-grey-800 disabled:bg-grey-300 disabled:cursor-not-allowed transition-all"
                >
                  {aiDescriptionLoading ? (
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                  ) : (
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Event Type */}
        <div className="space-y-2">
          <label
            htmlFor="eventType"
            className="block text-sm font-medium text-grey-900"
          >
            {t('createEvent.eventType')}
          </label>
          <select
            id="eventType"
            value={formState.eventType || "Festival"}
            onChange={(e) => setFormState(prev => ({ ...prev, eventType: e.target.value }))}
            className="w-full md:w-1/2 px-3 py-2.5 border border-grey-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent"
          >
            <option value="Festival">Festival</option>
            <option value="Concert">Concert</option>
            <option value="Sporting Event">Sporting Event</option>
            <option value="Farmers Market">Farmers Market</option>
            <option value="Art Show">Art Show</option>
            <option value="Food Fair">Food Fair</option>
            <option value="Community Event">Community Event</option>
          </select>
        </div>

        {/* Event Start and End Date & Time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Event Start Date & Time */}
          <DateTimeInput
            label={t('createEvent.eventStartDateTime')}
            dateValue={formState.startDate}
            onDateChange={formStateHandlers.startDate}
            timeValue={formState.startTime}
            onTimeChange={formStateHandlers.startTime}
            error={dateErrors.startDate}
            dateMin={getTodayDate()} // Use the minimum date logic if applicable
          />

          {/* Event End Date & Time */}
          <DateTimeInput
            label={t('createEvent.eventEndDateTime')}
            dateValue={formState.endDate}
            onDateChange={formStateHandlers.endDate}
            timeValue={formState.endTime}
            onTimeChange={formStateHandlers.endTime}
            error={dateErrors.endDate}
          />
        </div>

        {/* Recurring Event */}
        {/*<div className="space-y-2 mt-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formState.recurringEvent}
              onChange={formStateHandlers.recurringEvent}
              className="mr-2"
            />
            <span className="text-sm text-grey-900">Recurring Event</span>
          </label>

          {formState.recurringEvent && (
            <div className="relative inline-block w-6/12">
              <button
                className="w-full p-2 border border-grey-300 rounded-lg flex justify-start items-center"
                onClick={() =>
                  setFormState((prev) => ({
                    ...prev,
                    isRecurringEventDropdownOpen: !prev.isRecurringEventDropdownOpen,
                  }))
                }
              >
                <span className="flex-1 text-left">
                  {formState.selectedRecurringOption?.label || "Select"}
                </span>
                <ion-icon
                  name={
                    formState.isRecurringEventDropdownOpen ? "chevron-up" : "chevron-down"
                  }
                  size={16}
                  className="ml-2"
                />
              </button>

              {formState.isRecurringEventDropdownOpen && (
                <div className="absolute z-20 mt-2 w-full bg-white border border-grey-300 rounded-lg shadow-lg">
                  {recurringEventDropdownOptions.map((option) => (
                    <div
                      key={option.value}
                      className="p-2 hover:bg-grey-200 cursor-pointer"
                      onClick={() =>
                        setFormState((prev) => ({
                          ...prev,
                          selectedRecurringOption: option,
                          isRecurringEventDropdownOpen: false, // Close dropdown on selection
                        }))
                      }
                    >
                      <h4 className="font-bold text-sm">{option.label}</h4>
                      <p className="text-xs text-grey-600">{option.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>*/}

        {/* Application Deadline */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-grey-900">
            {t('createEvent.applicationDeadline')}
          </label>
          <input
            type="date"
            value={formState.applicationDeadline}
            onChange={formStateHandlers.applicationDeadline}
            className="w-full md:w-1/3 px-3 py-2.5 border border-grey-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent"
          />
          {dateErrors.applicationDeadline && (
            <p className="text-error-500 text-sm mt-1">
              {dateErrors.applicationDeadline}
            </p>
          )}
        </div>

        {/* Load In and Load Out Date-Time Pairs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Load In Date & Time */}
          <DateTimeInput
            label={t('createEvent.loadInDateTime')}
            dateValue={formState.loadInDate}
            onDateChange={formStateHandlers.loadInDate}
            timeValue={formState.loadInTime}
            onTimeChange={formStateHandlers.loadInTime}
            error={dateErrors.loadInDate}
          />

          {/* Load Out Date & Time */}
          <DateTimeInput
            label={t('createEvent.loadOutDateTime')}
            dateValue={formState.loadOutDate}
            onDateChange={formStateHandlers.loadOutDate}
            timeValue={formState.loadOutTime}
            onTimeChange={formStateHandlers.loadOutTime}
            error={dateErrors.loadOutDate}
          />
        </div>
      </div>

      {/* Social Links Section - Separate Card */}
      <div className="bg-white rounded-xl border border-grey-200 p-6 mt-6">
        <h3 className="text-sm font-medium text-grey-900 mb-4">{t('createEvent.socialLinks')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              label: "Website",
              value: formState.eoEventWebsiteURL,
              onChange: formStateHandlers.eoEventWebsiteURL,
              placeholder: "https://yourwebsite.com",
            },
            {
              label: "Facebook",
              value: formState.eoEventFacebookAccount,
              onChange: formStateHandlers.eoEventFacebookAccount,
              placeholder: "Facebook page URL",
            },
            {
              label: "Instagram",
              value: formState.eoEventInstagramAccount,
              onChange: formStateHandlers.eoEventInstagramAccount,
              placeholder: "@yourhandle",
            },
          ].map((field, index) => (
            <div key={index} className="space-y-1.5">
              <label className="block text-xs text-grey-500">{field.label}</label>
              <input
                type="text"
                value={field.value}
                onChange={field.onChange}
                className="w-full px-3 py-2 border border-grey-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder={field.placeholder}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row justify-end mt-6 sm:mt-8 gap-3">
        <button
          onClick={handleSaveForLaterClick}
          className="px-5 py-2.5 text-sm text-grey-600 hover:text-grey-800 transition"
        >
          {t('createEvent.saveDraft')}
        </button>
        <button
          onClick={handleSaveContinue}
          className={`px-6 py-2.5 text-sm font-medium text-white rounded-lg transition ${
            formState.eventName &&
            formState.location &&
            formState.applicationDeadline &&
            formState.startDate &&
            formState.endDate &&
            formState.loadInDate &&
            formState.loadOutDate &&
            Object.values(dateErrors).every((error) => !error)
              ? "bg-primary-600 hover:bg-primary-700"
              : "bg-grey-300 cursor-not-allowed"
          }`}
          disabled={
            !formState.eventName ||
            !formState.location ||
            !formState.applicationDeadline ||
            !formState.startDate ||
            !formState.endDate ||
            !formState.loadInDate ||
            !formState.loadOutDate ||
            Object.values(dateErrors).some((error) => error)
          }
        >
          {t('createEvent.continue')}
        </button>
      </div>
    </div>
  );
};

export default CreateEventContent1;

export const DateTimeInput = ({
  label,
  dateValue,
  onDateChange,
  timeValue,
  onTimeChange,
  error,
  dateMin,
}) => {
  return (
    <div className="flex-1 space-y-2">
      <label className="block text-sm font-medium text-grey-900">
        {label}
      </label>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="date"
          value={dateValue}
          min={dateMin}
          onChange={onDateChange}
          className={`flex-1 p-2 border ${error ? "border-error-500" : "border-grey-300"
            } rounded-lg`}
        />
        <input
          type="time"
          value={timeValue}
          onChange={onTimeChange}
          className={`flex-1 p-2 border ${error ? "border-error-500" : "border-grey-300"
            } rounded-lg`}
        />
      </div>
      {error && <p className="text-error-500 text-sm mt-1">{error}</p>}
    </div>
  );
};
